/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	AbstractControl
} from '@angular/forms';
import {
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormEventConstants
} from '@shared/constants/form-event.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	EventHelper
} from '@shared/helpers/event.helper';
import {
	StringHelper
} from './string.helper';

/**
 * A class containing static helper methods
 * for formly interactions.
 *
 * @export
 * @class FormlyHelper
 */
export class FormlyHelper
{
	/**
	 * Gets or sets the section index.
	 *
	 * @type {number}
	 * @memberof FormlyHelper
	 */
	private static sectionIndex: number;

	/**
	 * Gets or sets the tab index.
	 *
	 * @type {number}
	 * @memberof FormlyHelper
	 */
	private static tabIndex: number;

	/**
	 * Context-based tab state management to track which tabs have been
	 * added per context (e.g., per repeater instance).
	 * Key: context identifier, Value: whether first tab has been added
	 *
	 * @type {Map<string, boolean>}
	 * @memberof FormlyHelper
	 */
	private static readonly tabContextMap: Map<string, boolean> = new Map();

	/**
	 * Context-based tab index management to ensure unique tab IDs
	 * across different contexts.
	 * Key: context identifier, Value: current tab index for that context
	 *
	 * @type {Map<string, number>}
	 * @memberof FormlyHelper
	 */
	private static readonly tabIndexContextMap: Map<string, number> = new Map();

	/**
	 * This will add the sent additional method to the existing form controls
	 * update value and validity function.
	 * @note The fields logic check can likely be removed with an upgraded
	 * Formly.
	 * This was required starting with Formly v6.0.0-next.1.
	 *
	 * @static
	 * @param {AbstractControl} formControl
	 * The formly based form control that implements update value and validity.
	 * @param {Function} additionalMethod
	 * The additional method to run as part of the update value and validity
	 * chain of the sent control.
	 * @memberof FormlyHelper
	 */
	public static extendUpdateValueAndValidity(
		formControl: AbstractControl,
		additionalMethod: Function): void
	{
		// Ensure we are not cloning validations if the same field is displayed
		// in multiple locations due to anyOf displays.
		if ((<any>formControl)._fields?.length > 1)
		{
			delete (<any>formControl)._fields;
		}

		const currentValidityMethod: Function =
			formControl.updateValueAndValidity.bind(formControl);

		formControl.updateValueAndValidity =
			() =>
			{
				currentValidityMethod();
				additionalMethod();
			};
	}

	/**
	 * Fires a view check on a displayed formly form. This will reach into
	 * each form control in the layout and update the value and validity.
	 *
	 * @static
	 * @param {FormlyFieldConfig} formlyEntityLayout
	 * The formly field array to be recursively checked for validity.
	 * @param {boolean} dipatchRepeaterCleanModelEvent
	 * To dispatch the repeater clean model event. Defaults to false.
	 * @memberof FormlyHelper
	 */
	public static fireViewCheck(
		formlyEntityLayout: FormlyFieldConfig[],
		dipatchRepeaterCleanModelEvent: boolean = false): void
	{
		formlyEntityLayout.forEach(
			(field: FormlyFieldConfig) =>
			{
				if (AnyHelper.isNullOrWhitespace(
					field.formControl?.value)
					&& field.expressions?.hide?.valueOf() === true
					&& !AnyHelper.isNull(
						(<any>field.formControl)?.defaultValue))
				{
					field.formControl.setValue(
						(<any>field.formControl)?.defaultValue);
				}

				field.formControl?.markAsDirty();
				field.formControl?.updateValueAndValidity();
				this.updateChildren(field);
			});

		if (dipatchRepeaterCleanModelEvent === true)
		{
			EventHelper.dispatchRepeaterCleanModelEvent();
		}
	}

	/**
	 * Returns the parsed json object from the layout json data
	 * as Formly consumable layout fields. This is used for
	 * translations from the valid JSON in the database
	 * into a format the Formly expects.
	 *
	 * @static
	 * @param {FormlyFieldConfig[]} jsonLayout
	 * The formly field configuration to be created as a formly layout.
	 * @param {object} context
	 * The context for the formly layout. Primarily the component
	 * displaying the formly form, allowing component interactions from
	 * database entered json functions.
	 * @returns {FormlyFieldConfig[]}
	 * A formly field configuration array ready for display in a formly
	 * layout.
	 * @memberof FormlyHelper
	 */
	public static getFormlyLayout(
		jsonLayout: FormlyFieldConfig[],
		context: object): FormlyFieldConfig[]
	{
		// Clear context maps for fresh layout generation
		this.tabContextMap.clear();
		this.tabIndexContextMap.clear();

		this.sectionIndex = 0;
		this.tabIndex = 0;

		return jsonLayout.map(
			(field: FormlyFieldConfig,
				fieldIndex: number) =>
			{
				this.mapField(
					field,
					context,
					fieldIndex);

				return field;
			});
	}

	/**
	 * This will find the existing formly field config with a key
	 * matching the sent fieldKey.
	 *
	 * @param {FormlyFieldConfig[]} formlyEntityLayout
	 * The formly entity layout.
	 * @param {string} fieldKey
	 * The key of the field to acquire.
	 * @memberof FormlyHelper
	 * @returns {FormlyFieldConfig}
	 * If found, this will return the field config with the matching key.
	 */
	public static getMatchingFieldConfigurations(
		formlyEntityLayout: FormlyFieldConfig[],
		fieldKey: string): FormlyFieldConfig[]
	{
		let fields: FormlyFieldConfig[] = [];

		for (const layoutField of formlyEntityLayout)
		{
			const nestedFields: FormlyFieldConfig[] =
				this.getMatchingChildFieldConfigurations(
					layoutField,
					fieldKey);

			fields = fields.concat(nestedFields);
		}

		return fields;
	}

	/**
	 * This will find the existing child formly field config with a key
	 * matching the sent fieldKey.
	 *
	 * @param {FormlyFieldConfig} field
	 * The formly field.
	 * @param {string} fieldKey
	 * The key of the field to acquire.
	 * @memberof FormlyHelper
	 * @returns {FormlyFieldConfig}
	 * If found, this will return the field config with the child matching key.
	 */
	public static getMatchingChildFieldConfigurations(
		field: FormlyFieldConfig,
		fieldKey: string): FormlyFieldConfig[]
	{
		let fields: FormlyFieldConfig[] = [];

		if (!AnyHelper.isNull(field.props?.attributes)
			&& field.props.attributes[
				FormlyConstants.attributeKeys.dataKey]?.toString()
				=== fieldKey
			|| field.key?.toString() === fieldKey)
		{
			fields.push(field);
		}

		const fieldGroups: any[] =
			(field.fieldGroup || []);

		for (const fieldGroup of fieldGroups)
		{
			fields = fields.concat(this.getMatchingChildFieldConfigurations(
				fieldGroup,
				fieldKey));
		}

		return fields;
	}

	/**
	 * This will check a formly field to see if this field is describing a tab
	 * content wrapper.
	 *
	 * @param {FormlyFieldConfig} field
	 * The formly field configuration to check for a tab content wrapper.
	 * @return {boolean}
	 * A value signifying whether or not this field is a tab content wrapper.
	 * @memberof FormlyHelper
	 */
	public static isTabWrapper(
		field: FormlyFieldConfig): boolean
	{
		const wrapperTypes: string[] =
			<string[]>
			[
				FormlyConstants.customControls.customTabContent,
				FormlyConstants.customControls.customNestedTabContent
			];

		return wrapperTypes.some(
			(wrapperType: string) =>
				field.wrappers?.indexOf(wrapperType) >= 0);
	}

	/**
	 * Strips layout-type path segments (segments prefixed with '__') from a
	 * dot-separated key path. Custom repeater layouts use virtual grouping keys
	 * such as __dwellings or __otherStructures to organize by asset type,
	 * but these segments do not exist in the underlying data model. Stripping
	 * them normalizes the key so that rule presentation dataKey lookups and
	 * navigation always match regardless of which layout group a field is
	 * rendered inside.
	 *
	 * @static
	 * @param {string} key
	 * The dot-separated key path to normalize.
	 * @returns {string}
	 * The key path with all '__'-prefixed segments removed.
	 * @memberof FormlyHelper
	 */
	public static stripLayoutTypeSegments(
		key: string): string
	{
		return key
			.split(
				AppConstants.characters.period)
			.filter(
				(segment: string) =>
					!segment.startsWith('__'))
			.join(
				AppConstants.characters.period);
	}

	/**
	 * Determines if the feild should be visible.
	 *
	 * @param {FormlyFieldConfig} field
	 * The field to check.
	 * @param {any} context
	 * the page context.
	 * @returns {Promise<boolean>}
	 * A promise containg a bollean of true if the field should be visible.
	 * @async
	 * @memberof FormlyHelper
	 */
	public static async visible(
		field: FormlyFieldConfig,
		context: any): Promise<boolean>
	{
		if (AnyHelper.isNull(field.props))
		{
			return true;
		}

		if (AnyHelper.isNullOrEmpty(
			field.props.displayPromise))
		{
			return AnyHelper.isNullOrEmpty(field.props.display)
				? true
				: field.props.display;
		}

		return StringHelper
			.transformToDataPromise(
				field.props.displayPromise,
				context);
	}

	/**
	 * This will check a formly field to see if this field is describing a
	 * custom empty wrapper
	 *
	 * @param {FormlyFieldConfig} field
	 * The formly field configuration to check for a tab content wrapper.
	 * @return {boolean}
	 * A value signifying whether or not this field is a tab content wrapper.
	 * @memberof FormlyHelper
	 */
	public static isEmptyWrapper(
		field: FormlyFieldConfig): boolean
	{
		const wrapperTypes: string[] =
			<string[]>
			[
				FormlyConstants.customControls.customEmptyWrapper
			];

		return wrapperTypes.some(
			(wrapperType: string) =>
				field.wrappers?.indexOf(wrapperType) >= 0);
	}

	/**
	 * This will check a formly field to see if this field is describing a
	 * custom field wrapper.
	 *
	 * @param {FormlyFieldConfig} field
	 * The formly field configuration to check for a custom field wrapper.
	 * @return {boolean}
	 * A value signifying whether or not this field is a custom field wrapper.
	 * @memberof FormlyHelper
	 */
	public static isCustomFieldWrapper(
		field: FormlyFieldConfig): boolean
	{
		const wrapperTypes: string[] =
			<string[]>
			[
				FormlyConstants.customControls.customFieldWrapper
			];

		return wrapperTypes.some(
			(wrapperType: string) =>
				field.wrappers?.indexOf(wrapperType) >= 0);
	}

	/**
	 * This will check a formly field to see if this field is describing
	 * nested field group.
	 *
	 * @param {FormlyFieldConfig} field
	 * The formly field configuration to check for a nested field group.
	 * @return {boolean}
	 * A value signifying whether or not this field has a nested field group.
	 * @memberof FormlyHelper
	 */
	public static hasNestedFieldGroup(
		field: FormlyFieldConfig): boolean
	{
		return (!AnyHelper.isNullOrEmptyArray(
			(<any>field.fieldArray)?.fieldGroup)
			|| !AnyHelper.isNullOrEmptyArray(field.fieldGroup));
	}

	/**
	 * This will find and set the provided value to the formly field config with
	 * a key matching the sent fieldKey into the formly layout.
	 *
	 * @param {FormlyFieldConfig[]} jsonLayout
	 * The formly field configuration to be created as a formly layout.
	 * @param {string} fieldKey
	 * The key of the field to acquire.
	 * @param {any} fieldValue
	 * The value of the field to acquire.
	 * @param {boolean} fireValidityChange
	 * Executes the validity of the form if true, otherwise the
	 * validity is skipped.
	 * @memberof FormlyHelper
	 */
	public static setFieldValue(
		jsonLayout: FormlyFieldConfig[],
		fieldKey: string,
		fieldValue: any = AppConstants.empty,
		fireValidityChange: boolean = false): void
	{
		const field: FormlyFieldConfig = jsonLayout
			.find((fieldConfig) =>
				fieldConfig.key === fieldKey);

		field.formControl?.setValue(fieldValue);

		if (fireValidityChange)
		{
			field.formControl?.markAsDirty();
			field.formControl?.markAsTouched();
			field.formControl?.updateValueAndValidity();
		}

		const element: any = document.getElementById(
			field.id);

		element?.dispatchEvent(new Event(FormEventConstants.onChangeEvent));
	}

	/**
	 * Translates string based synchronous or asynchronous validations
	 * into runnable functions available in formly.
	 *
	 * @static
	 * @param {any} validationSet
	 * The synchronous or asynchronous validations that need mapped.
	 * @param {object} context
	 * The context for the formly layout. Primarily the component
	 * displaying the formly form, allowing component interactions from
	 * database entered json functions.
	 * @returns {any}
	 * The mapped and formly available validator set running as functions
	 * again the sent context.
	 * @memberof FormlyHelper
	 */
	public static getValidatorFunctions(
		validationSet: any,
		context: object): any
	{
		if (!AnyHelper.isNullOrEmpty(validationSet))
		{
			Object.keys(validationSet)
				.forEach((key: string) =>
				{
					if (validationSet[key].expression
						&& typeof validationSet[key].expression ===
							AppConstants.variableTypes.string)
					{
						validationSet[key].expression =
							Function(
								'control',
								'field',
								validationSet[key].expression)
								.bind(context);
					}
				});
		}

		return validationSet;
	}

	/**
	 * Given a sent formly layout this will disable all fields and nested
	 * fields found in this layout.
	 *
	 * @static
	 * @param {FormlyFieldConfig[]} formlyEntityLayout
	 * The formly field array to have all fields and nested fields set as
	 * disabled.
	 * @param {boolean} disabled
	 * The set to disable all fields or if false then enable.
	 * Default to true.
	 * @param {boolean} clearDisabledExpressions
	 * If sent and true, this will also remove any expressions of props.disabled
	 * to enforce disables or enables.
	 * @returns {FormlyFieldConfig[]}
	 * The formly field array with all fields and nested fields set as the
	 * parameter disabled.
	 * @memberof FormlyHelper
	 */
	public static disableAllFields(
		formlyEntityLayout: FormlyFieldConfig[],
		disabled: boolean = true,
		clearDisabledExpressions: boolean = false): FormlyFieldConfig[]
	{
		formlyEntityLayout.forEach(
			(field: FormlyFieldConfig) =>
			{
				if (clearDisabledExpressions === true)
				{
					this.clearDisabledExpressions(field);
				}

				field.props.disabled = disabled;
				this.disableChildren(
					field,
					disabled,
					clearDisabledExpressions);
			});

		return formlyEntityLayout;
	}

	/**
	 * Updates a Formly field's props based on the number of options.
	 * Disables the field if no options are available.
	 * Sets it to the first option if only one is available.
	 * Otherwise, the field remains enabled for selection.
	 *
	 * @static
	 * @param {FormlyFieldConfig} field
	 * The Formly field configuration to update.
	 * @param {any[]} options
	 * Array of available options, affecting field behavior based on length.
	 * @param {string} [valueKey]
	 * Key in the option object to set as the field value.
	 * @memberof FormlyHelper
	 */
	public static updateFieldBasedOnOptions(
		field: FormlyFieldConfig,
		options: any[],
		valueKey: string = 'id'): void
	{
		if (options.length === 0)
		{
			field.props.disabled = true;
			field.props.placeholder = 'No Available Options';
		}
		else if (options.length === 1)
		{
			field.props.disabled = false;
			field.formControl.setValue(String(options[0][valueKey]));
			field.props.disabled = true;
			field.formControl.updateValueAndValidity();
		}
		else
		{
			field.props.placeholder = 'Select an Option';
			field.formControl.updateValueAndValidity();
		}
	}

	/**
	 * This will validate all json code editors found in the formly layout.
	 *
	 * @static
	 * @param {FormlyFieldConfig[]} formlyLayout
	 * The formly field array to be checked for valid json code editors.
	 * @param {string[]} fieldConfigurations
	 * The field configurations to be checked for valid json code editors.
	 * @returns {boolean[]}
	 * An array of boolean values representing the validity of each json code
	 * editor.
	 * @memberof FormlyHelper
	 */
	public static validateJsonCodeEditors(
		formlyLayout: FormlyFieldConfig[],
		fieldConfigurations: string[]): boolean[]
	{
		const validFields: boolean[] = [];

		for (const fieldConfiguration of fieldConfigurations)
		{
			const jsonFields: FormlyFieldConfig[] =
				this.getMatchingFieldConfigurations(
					formlyLayout,
					fieldConfiguration)
					.filter(
						(field: FormlyFieldConfig) =>
							AnyHelper.isFunction(field.props.isValidJson));

			let isValid: boolean = true;
			for (const jsonField of jsonFields)
			{
				isValid =
					isValid && jsonField.props.isValidJson();
			}

			validFields.push(isValid);
		}

		return validFields;
	}

	/**
	 * This will create a standard error message for layouts that hold invalid
	 * code editors.
	 *
	 * @static
	 * @param {boolean[]} validCodeEditors
	 * The array of valid code editor results.
	 * @param {string[]} codeEditorDisplayNames
	 * The display names of code editors in an array with index matched
	 * display names to validity based booleans found in the valid code
	 * editors parameter.
	 * @returns {string}
	 * A string value that represents the error message for invalid code
	 * editors.
	 * @memberof FormlyHelper
	 */
	public static generateJsonCodeEditorValidationMessage(
		validCodeEditors: boolean[],
		codeEditorDisplayNames: string[]): string
	{
		const editorDisplayNames: string[] = [];

		validCodeEditors.forEach(
			(codeEditorValid: boolean,
				index: number) =>
			{
				if (codeEditorValid === false)
				{
					editorDisplayNames.push(
						`'${codeEditorDisplayNames[index]}'`);
				}
			});

		const invalidControlCount: number =
			editorDisplayNames.length;

		const message: string =
			StringHelper.formatStringArrayToPartialSentence(
				editorDisplayNames);

		const plurality: string =
			invalidControlCount === 1
				? 'Json Control'
				: 'Json Controls';
		const pluralityPreposition: string =
			invalidControlCount === 1
				? 'is'
				: 'are';

		return `The ${plurality} ${message} ${pluralityPreposition} invalid `
			+ 'and must be corrected before saving.';
	}

	/**
	 * Given a sent formly layout this will disable all fields and nested
	 * fields found in this layout. This disable will be called recursively
	 * for each child of the sent field.
	 *
	 * @static
	 * @param {FormlyFieldConfig} field
	 * The formly field to be set as disabled along with all children.
	 * @param {boolean} disabled
	 * The set to disable all fields or if false then enable.
	 * Default to true.
	 * @param {boolean} clearDisabledExpressions
	 * If sent and true, this will also remove any expressions of props.disabled
	 * to enforce disables or enables.
	 * @memberof FormlyHelper
	 */
	private static disableChildren(
		field: FormlyFieldConfig,
		disabled: boolean,
		clearDisabledExpressions: boolean = false): void
	{
		field.fieldGroup?.forEach(
			(fieldGroup: FormlyFieldConfig) =>
			{
				if (clearDisabledExpressions === true)
				{
					this.clearDisabledExpressions(fieldGroup);
				}

				fieldGroup.props.disabled = disabled;
				this.disableChildren(
					fieldGroup,
					disabled,
					clearDisabledExpressions);
			});

		(<FormlyFieldConfig[]>(<any>field.fieldArray)?.fieldGroup)?.forEach(
			(fieldGroup: FormlyFieldConfig) =>
			{
				if (clearDisabledExpressions === true)
				{
					this.clearDisabledExpressions(fieldGroup);
				}

				fieldGroup.props.disabled = disabled;
				this.disableChildren(
					fieldGroup,
					disabled,
					clearDisabledExpressions);
			});
	}

	/**
	 * Given a sent formly layout this will clear any disabled expressions
	 * found in this layout. This clear will be called recursively for each
	 * child of the sent field.
	 *
	 * @static
	 * @param {FormlyFieldConfig} field
	 * The formly field to be set as disabled along with all children.
	 * @memberof FormlyHelper
	 */
	private static clearDisabledExpressions(
		field: FormlyFieldConfig): void
	{
		if (!AnyHelper.isNullOrEmpty(field.expressions)
			&& !AnyHelper.isNullOrEmpty(
				field.expressions[
					FormlyConstants.expressionMappings.propsDisabled]))
		{
			delete field.expressions[
				FormlyConstants.expressionMappings.propsDisabled];
		}
	}

	/**
	 * Recursively calls an update of value and validity for each nested
	 * set of form controls.
	 *
	 * @param {FormlyFieldConfig} field
	 * The formly field to be recursively checked for validity.
	 * @memberof FormlyHelper
	 */
	private static updateChildren(
		field: FormlyFieldConfig): void
	{
		field.fieldGroup?.forEach(
			(fieldGroup: FormlyFieldConfig) =>
			{
				if (AnyHelper.isNullOrWhitespace(
					fieldGroup.formControl?.value)
					&& fieldGroup.expressions?.hide?.valueOf() === true
					&& !AnyHelper.isNull(
						(<any>fieldGroup.formControl)?.defaultValue))
				{
					fieldGroup.formControl.setValue(
						(<any>fieldGroup.formControl)?.defaultValue);
				}

				fieldGroup.formControl?.markAsDirty();
				fieldGroup.formControl?.updateValueAndValidity();
				this.updateChildren(fieldGroup);
			});
	}

	/**
	 * Recursively maps children into a converted formly field.
	 *
	 * @static
	 * @param {FormlyFieldConfig} field
	 * The formly field to be recursively converted and mapped.
	 * @param {object} context
	 * The context for the formly layout. Primarily the component
	 * displaying the formly form, allowing component interactions from
	 * database entered json functions.
	 * @param {number} fieldIndex
	 * field index position in the array.
	 * @param {string} nestedLocation
	 * If sent this will handle the convert with the nested field identifier.
	 * @memberof FormlyHelper
	 */
	private static mapField(
		field: FormlyFieldConfig,
		context: object,
		fieldIndex: number,
		nestedLocation: string = null): void
	{
		if (FormlyHelper.isTabWrapper(field)
			|| (!FormlyHelper.isCustomFieldWrapper(field)
				&& FormlyHelper.hasNestedFieldGroup(field))
			|| field.type === FormlyConstants.customControls.customSectionTitle)
		{
			this.sectionIndex ++;
		}

		if (field.type !== FormlyConstants.customControls.customSectionTitle
			&& (!FormlyHelper.isEmptyWrapper(field)
				|| FormlyHelper.isEmptyWrapper(field)
					&& AnyHelper.isNull(field.props))
			&& AnyHelper.isNull(field.key)
			&& !FormlyHelper.isTabWrapper(field))
		{
			return;
		}

		this.convertToFormlyField(
			field,
			context,
			fieldIndex,
			nestedLocation);

		const fieldKeyString: string = field.key?.toString();
		let repeaterKey: string;

		if (AnyHelper.isNullOrWhitespace(fieldKeyString))
		{
			// Keyless wrapper — pass the parent path through unchanged.
			repeaterKey = nestedLocation ?? AppConstants.empty;
		}
		else if (AnyHelper.isNullOrWhitespace(nestedLocation))
		{
			repeaterKey = fieldKeyString;
		}
		else
		{
			repeaterKey = `${nestedLocation}.${fieldKeyString}`;
		}

		this.setActionPerFieldGroup(
			field?.fieldGroup,
			context,
			repeaterKey);

		this.setActionPerFieldGroup(
			(<any>field.fieldArray)?.fieldGroup,
			context,
			repeaterKey);

		if (FormlyHelper.isTabWrapper(field))
		{
			const tabContext: string = this.getTabContext(nestedLocation);
			const contextTabAdded: boolean =
				this.tabContextMap.get(tabContext) ?? false;

			field.props.visible = !contextTabAdded;

			if (contextTabAdded === false)
			{
				this.tabContextMap.set(tabContext, true);
			}

			const currentTabIndex: number =
				this.tabIndexContextMap.get(tabContext) ?? 0;
			this.tabIndexContextMap.set(tabContext, currentTabIndex + 1);

			this.tabIndex ++;
		}
	}

	/**
	 * For a sent formly field pulled from the layout, this will
	 * alter stored JSON into a format that Formly expects.
	 *
	 * @static
	 * @param {FormlyFieldConfig} field
	 * The layout field to be cast into a Formly field for display.
	 * @param {object} context
	 * The context for the formly layout. Primarily the component
	 * displaying the formly form, allowing component interactions from
	 * database entered json functions.
	 * @param {number} fieldIndex
	 * field index position in the array.
	 * @param {string} nestedLocation
	 * If sent this will handle the convert with the nested field identifier.
	 * @memberof FormlyHelper
	 */
	private static convertToFormlyField(
		field: FormlyFieldConfig,
		context: object,
		fieldIndex: number,
		nestedLocation: string = null): FormlyFieldConfig
	{
		field.props = field.props ?? {};

		if (!AnyHelper.isNullOrWhitespace(field.key))
		{
			field.props.attributes =
				{
					'data-key':
						FormlyHelper.stripLayoutTypeSegments(
							((AnyHelper.isNullOrWhitespace(nestedLocation)
								? AppConstants.empty
								: `${nestedLocation}.`)
									+ field.key)
								.replaceAll(
									/\[\d+\]/g,
									AppConstants.empty))
				};
		}

		field.props.attributes =
			{
				...field.props.attributes,
				'section-index': this.sectionIndex,
				'tab-index': this.tabIndex,
				'field-index': fieldIndex
			};

		field.validators =
			this.getValidatorFunctions(
				field.validators,
				context);
		field.asyncValidators =
			this.getValidatorFunctions(
				field.asyncValidators,
				context);

		if (!AnyHelper.isNullOrWhitespace(field.props.change)
			&& !AnyHelper.isFunction(field.props.change))
		{
			field.props.change =
				new Function(
					'field',
					'$event',
					field.props.change.toString())
					.bind(context);
		}

		if (!AnyHelper.isNullOrWhitespace(field.props.updateDataOptions)
			&& !AnyHelper.isFunction(field.props.updateDataOptions))
		{
			field.props.updateDataOptions =
				new Function(
					field.props.updateDataOptions.toString())
					.bind(context);
		}

		return field;
	}

	/**
	 * For a sent formly field pulled from the layout, this will
	 * alter stored JSON into a format that Formly expects.
	 *
	 * @static
	 * @param {FormlyFieldConfig[]} fieldGroups
	 * The layout field groups.
	 * @param {object} context
	 * The context for the formly layout. Primarily the component
	 * displaying the formly form, allowing component interactions from
	 * database entered json functions.
	 * @param {string} repeaterKey
	 * The repeater key.
	 * @memberof FormlyHelper
	 */
	 private static setActionPerFieldGroup(
		fieldGroups: FormlyFieldConfig[],
		context: object,
		repeaterKey: string): void
	{
		fieldGroups?.forEach(
			(fieldGroup: FormlyFieldConfig,
				fieldIndex: number) =>
			{
				this.convertToFormlyField(
					fieldGroup,
					context,
					fieldIndex,
					repeaterKey);

				this.mapField(
					fieldGroup,
					context,
					fieldIndex,
					repeaterKey);
			});
	}

	/**
	 * Generates a unique context key for tab state management.
	 * This is used to isolate tab visibility and index management
	 * between different contexts (e.g., different repeater instances).
	 *
	 * @static
	 * @param {string} nestedLocation
	 * The nested location path if within a repeater context.
	 * @returns {string}
	 * A unique context identifier for tab state management.
	 * @memberof FormlyHelper
	 */
	private static getTabContext(
		nestedLocation?: string): string
	{
		// For root level tabs, use a default context
		if (AnyHelper.isNullOrWhitespace(nestedLocation))
		{
			return FormlyConstants.contextIdentifiers.root;
		}

		// For nested contexts (repeaters), use the nested location
		// This ensures each repeater instance has its own tab state
		return nestedLocation;
	}
}